import { useFormik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { UserIcon } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';
import { userApi } from '../../../repositories/user';
import { handleError } from '../../../utils/errorHandler';
import Loader from '../../../components/common/Loader';
import EditPasswordModal from '../../../components/modal/EditPasswordModal';
import { useEffect, useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

interface ProfileData {
  id: number;
  fullName: string;
  email: string;
  role: string;
  memberSince: string;
  lastLogin: string;
  associatedLabel: string;
  profilePicture: string | null;
}

interface PasswordFormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const validationSchema = Yup.object({
  fullName: Yup.string().required('Full name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
});

const Profile: React.FC = () => {
  const [profileData, setProfileData] = useState<ProfileData>({
    id: 0,
    fullName: '',
    email: '',
    role: '',
    memberSince: '',
    lastLogin: '',
    associatedLabel: '',
    profilePicture: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState<boolean>(false);
  const navigate = useNavigate();
  const fetchProfile = async () => {
      try {
        const response = await userApi.getProfile();
        
        if (!response?.data?.success) {
          throw new Error(response?.data?.error || 'Failed to fetch profile');
        }
        const data = response.data.data;
        const memberSinceDate = new Date(data.createdAt || Date.now());
        const lastLoginDate = data.lastLogin ? new Date(data.lastLogin) : null;
        setProfileData({
          id: data.id || 0,
          fullName: data.fullName || '',
          email: data.email || '',
          role: data.role || 'basic',
          memberSince: format(memberSinceDate, 'MMMM d, yyyy'),
          lastLogin: lastLoginDate
            ? format(lastLoginDate, 'MMMM d, yyyy, h:mm a')
            : 'Never logged in',
          associatedLabel: data.Label?.name || 'No label associated',
          profilePicture: data.profilePicture || null,
        });
        formik.setValues({
          fullName: data.fullName || '',
          email: data.email || '',
        });
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    }
  
  useEffect(() => {
    fetchProfile();
    
  }, []);

  const formik = useFormik({
    initialValues: {
      fullName: profileData.fullName,
      email: profileData.email,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isFormChanged) {
          const response = await userApi.updateProfile({
            fullName: values.fullName,
            email: values.email,
          });
          if (!response?.data?.success) {
            throw new Error(response?.data?.error || 'Failed to update profile');
          }
          setProfileData({
            ...profileData,
            fullName: values.fullName,
            email: values.email,
          });
          setIsFormChanged(false);
          Swal.fire({
            title: 'Success!',
            text: response.data.message || 'Profile updated successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
          });
        } else {
          Swal.fire({
            title: 'No changes',
            text: 'No changes were made to your profile.',
            icon: 'info',
            confirmButtonText: 'OK',
          });
        }
      } catch (error) {
        handleError(error);
      }
    },
  });

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        Swal.fire({
          title: 'Error',
          text: 'Please select a valid image file (JPG, PNG, GIF, BMP, WEBP, SVG).',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire({
          title: 'Error',
          text: 'File size exceeds 10MB limit.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }
      setSelectedFile(file);
      setImageError(false);
    } else {
      Swal.fire({
        title: 'Error',
        text: 'No file selected.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      Swal.fire({
        title: 'Error',
        text: 'Please select an image to upload.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    try {
      Swal.fire({
        title: 'Uploading...',
        text: 'Please wait while your image is being uploaded.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const formData = new FormData();
      formData.append('profilePicture', selectedFile);

      const response = await userApi.uploadProfilePicture(formData);
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Failed to upload profile picture');
      }

      setProfileData({
        ...profileData,
        profilePicture: response.data.data.profilePicture || null,
      });

      setSelectedFile(null);
      setImageError(false);

      Swal.fire({
        title: 'Success!',
        text: response.data.message || 'Profile picture uploaded successfully.',
        icon: 'success',
        confirmButtonText: 'OK',
      });
    } catch (error:any) {
      Swal.close();
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to upload profile picture.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleFormChange = (e: ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    setIsFormChanged(true);
  };

  const handlePasswordSubmit = async (
    values: PasswordFormValues,
    { setSubmitting, resetForm }: any
  ) => {
    try {
      const response = await userApi.updatePassword({
        oldPassword: values.oldPassword,
        password: values.newPassword,
      });
      if (!response?.data?.success) {
        throw new Error(response?.data?.error || 'Failed to update password');
      }
      Swal.fire({
        title: 'Success!',
        text: response.data.message || 'Password updated successfully.',
        icon: 'success',
        confirmButtonText: 'OK',
      });
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      handleError(error);
    } finally {
      setSubmitting(false);
    }
  };


  const handleDeleteAccount = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        Swal.fire({
          title: 'Deleting...',
          text: 'Please wait while your account is being deleted.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const response = await userApi.deleteAccount(profileData.id);
        if (!response?.data?.success) {
          throw new Error(response?.data?.error || 'Failed to delete account');
        }

        Swal.fire({
          title: 'Deleted!',
          text: response.data.message || 'Your account has been deleted.',
          icon: 'success',
          confirmButtonText: 'OK',
        });
        navigate('/auth/signup');
      } catch (error) {
        Swal.close();
        handleError(error);
      }
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="max-w-3xl p-4 bg-white rounded-lg shadow-sm sm:p-6">
      <h2 className="mb-4 text-base font-semibold text-gray-800 sm:text-lg">User Profile</h2>
      <hr className="mb-4 border-gray-200 sm:mb-6" />

      <form onSubmit={formik.handleSubmit}>
        <div className="flex flex-col">
          <div className="flex flex-col items-center mb-4 sm:mb-6">
            <div className="flex items-center justify-center w-16 h-16 overflow-hidden bg-gray-200 rounded-full sm:w-20 sm:h-20">
              {(profileData.profilePicture && !imageError) ? (
                <img
                  src={profileData.profilePicture}
                  alt="Profile"
                  className="object-cover w-full h-full"
                  onError={() => setImageError(true)}
                />
              ) : (
                <UserIcon className="w-8 h-8 text-gray-500 sm:w-10 sm:h-10" />
              )}
            </div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/bmp,image/webp,image/svg+xml"
              onChange={handleFileChange}
              className="mt-2 text-xs text-gray-600 sm:text-sm"
              id="profilePictureInput"
              hidden
            />
            <label
              htmlFor="profilePictureInput"
              className="mt-2 sm:mt-4 bg-blue-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm hover:bg-blue-700 w-32 sm:w-36 text-center cursor-pointer"
            >
              Select Image
            </label>
            {selectedFile && (
              <button
                type="button"
                onClick={handleUpload}
                className="mt-2 bg-green-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm hover:bg-green-700 w-32 sm:w-36"
              >
                Upload Image
              </button>
            )}
          </div>

          <div className="mb-4">
            <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Member Since</label>
            <p className="text-xs text-gray-600 sm:text-sm">{profileData.memberSince}</p>
          </div>

          <div className="mb-4 sm:mb-6">
            <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Last Login</label>
            <p className="text-xs text-gray-600 sm:text-sm">{profileData.lastLogin}</p>
          </div>

          <div className="mb-4">
            <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Full Name</label>
            <input
              type="text"
              name="fullName"
              value={formik.values.fullName}
              onChange={handleFormChange}
              onBlur={formik.handleBlur}
              className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            {formik.touched.fullName && formik.errors.fullName ? (
              <p className="mt-1 text-xs text-red-500">{formik.errors.fullName}</p>
            ) : null}
          </div>

          <div className="mb-4">
            <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Email Address</label>
            <input
              type="email"
              name="email"
              value={formik.values.email}
              onChange={handleFormChange}
              onBlur={formik.handleBlur}
              className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            {formik.touched.email && formik.errors.email ? (
              <p className="mt-1 text-xs text-red-500">{formik.errors.email}</p>
            ) : null}
          </div>
              
          <div className="mb-4">
            <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Role</label>
            <div className="relative">
              <input
                type="text"
                value={profileData.role ==="basic" ? "user":profileData.role}
                className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                readOnly
              />
            </div>
          </div>

          <div className="mb-4 sm:mb-6">
            <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Associated Label</label>
            <input
              type="text"
              value={profileData.associatedLabel}
              className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              readOnly
            />
          </div>

          <div className="flex flex-col mb-4 space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4 sm:mb-6">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="w-full px-4 py-2 text-xs text-center text-blue-600 border border-gray-300 rounded-md sm:flex-1 hover:bg-gray-50 sm:text-sm"
            >
              Change Password
            </button>
            <button
              type="button"
              onClick={handleDeleteAccount}
              className="w-full px-4 py-2 text-xs text-center text-red-600 border border-red-300 rounded-md sm:flex-1 hover:bg-red-50 sm:text-sm"
            >
              Delete Account
            </button>
          </div>

          <button
            type="submit"
            className="w-full py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
            disabled={!isFormChanged}
          >
            Save Changes
          </button>
        </div>
      </form>

      <EditPasswordModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handlePasswordSubmit}
      />
    </div>
  );
};

export default Profile;